import {Injectable, OnDestroy} from '@angular/core';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {FilterCriteria} from '@docma-mm-core/filter/filter-criteria';
import {Layout} from '@docma-mm-core/layout/layout';
import {LayoutSortItem} from '@docma-mm-core/layout/layout-sort-item';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {Project} from '@docma-mm-core/project/project';
import {isIdsEquals} from '@docma-mm-core/utils/utils';
import {AppContainerService, ContentSideNavButton, PendingService} from '@mydocma/app-container';
import {BehaviorSubject, firstValueFrom, of} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {MmProjectDownloadService} from '../../mm-project/mm-download.service';
import {MmProjectLoaderService} from '../../mm-project/mm-project-loader.service';
import {MmViewService} from '../../mm-view.service';
import {FieldOrder, LayoutStateModel} from './layout-state.model';
import {buttonsMap} from '../../mm-sidenavbar/mm-sidnavbar-buttons.maps';
import {MmSideNavBarButtonsEnum} from '../../mm-sidenavbar/mm-sidenavbar-buttons.enum';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {GroupByCol, GroupSortType} from '@mydocma/component-library/src/lib/grid';
import {LoadLayoutFaultsOptions} from '../../mm-faults/load-layout-faults-options.enum';
import {GridApi} from 'ag-grid-community';
import {MmProjectService} from '../../mm-project/mm-project.service';
import {FilterService} from '../../mm-faults/mm-faults-ag-grid/filters/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MmRoutesEnum} from '../../mm-routing/mm-routes.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class MmLayoutStateService implements OnDestroy {


  public currentLayoutStateSubject = new BehaviorSubject<{state: LayoutStateModel, loadOptions: LoadLayoutFaultsOptions}>({state: null, loadOptions: LoadLayoutFaultsOptions.None}) ;
  public currentLayoutState$ = this.currentLayoutStateSubject.asObservable();

  private initializedSubject = new BehaviorSubject<boolean>(false);
  private loadingLayoutDef = new BehaviorSubject<boolean>(false);

  public initialized = this.initializedSubject.asObservable();
  public loading = this.loadingLayoutDef.asObservable();

  userStateChangedSubject = new BehaviorSubject<Layout>(null);
  userStateChanged$ = this.userStateChangedSubject.asObservable();

  columnOrderChangedSubject = new BehaviorSubject<Layout>(null);
  columnOrderChanged$ = this.columnOrderChangedSubject.asObservable();

  private prevState;
  private prevSelectedLayout;

  defaultLayoutHasNoFields = false;

  public gridApi: GridApi;
  public filterService: FilterService;

  constructor(private readonly mmViewService: MmViewService,
              private readonly pendingService: PendingService,
              private readonly mmLoaderService: MmProjectLoaderService,
              private readonly mmDownloadService: MmProjectDownloadService,
              private readonly personalizationService: PersonalizationService,
              private readonly appContainerService: AppContainerService,
              private readonly deviceService: DeviceService,
              private readonly mmProjectService: MmProjectService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly router: Router,
              private readonly logger: Logger) {

    this.pendingService.registerPendingState(this.loadingLayoutDef);

    this.mmViewService.currentProject$
      .pipe(
        switchMap(project => this.mmLoaderService.project.pipe(filter(f => f.loadState?.status === LoadStatusEnum.Loaded)).pipe(
          switchMap(loadedProject =>
              project && project.MyDocmaUid === loadedProject.data.MyDocmaUid ?
                  this.mmLoaderService.layouts.pipe(
                      filter(ls => ls && ls.loadState.status === LoadStatusEnum.Loaded ),
                      map(layouts => ({project, layouts: layouts.data}))
                  ) :
                  of({project, layouts: []} )
          ))
        ),
        switchMap(p => this.loadingLayoutDef.pipe(
          filter(l => !l ),
          map(l => p),

        ))
      ).subscribe(async p => {
        if (this.prevState === p) {
          return;
        }
        this.loadingLayoutDef.next(true);
        try {
          await this.initialize(p.project, p.layouts);
        } catch (err) {
          this.logger.error(err, '', 'MmLayoutStateService');
        }
        this.prevState = p;
        this.loadingLayoutDef.next(false);
      } );

  }

  private async initialize(project: Project, layouts: Layout[]) {
    const prevState = this.currentLayoutStateSubject.value.state;
    const state = new LayoutStateModel();
    state.project = project;

    if (project) {
      // if (prevState?.project?.MyDocmaUid === project.MyDocmaUid) {
      //   return;
      // }
      let layout;
      if (layouts.length > 0) {
        // MMW-1297
        // 1. Try: Keep layout from previous project if available in new project
        layout = layouts?.filter(l => l.Scope === EntityScopeEnum.Global || l.Scope === EntityScopeEnum.User).find(l => l.Id === this.prevSelectedLayout?.Id);
        layout = layout ?? layouts.filter(l => l.Scope === EntityScopeEnum.Project && l.Name).find(l => l.Name === this.prevSelectedLayout?.Name);
        if (!layout) {
          // 2. Try: Restore last used layout from index db
          const lastLayoutId = await this.personalizationService.getProjectValue(project.MyDocmaUid, PersonalizationKeysEnum.LastLayoutId);
          layout = layouts.find(l => l.Id === lastLayoutId?.Id);
        }

        if (!layout) {
          // 3. Try: Select first fav layout (Project - User - Global)
          layout = layouts.find(l => l.Scope === EntityScopeEnum.Project && (l.IsFavoriteUser || l.IsFavoriteAll));
          layout = layout ?? layouts.find(l => l.Scope === EntityScopeEnum.User && (l.IsFavoriteUser || l.IsFavoriteAll));
          layout = layout ?? layouts.find(l => l.IsFavoriteUser || l.IsFavoriteAll);
        }
        if (!layout) {
          // 4. Try: Select first project layout
          layout = layouts.find(l => l.Scope === EntityScopeEnum.Project);
        }
        if (!layout) {
          // 5. Try: Select first global layout and 6. Try: just pick the first layout
          layout = layouts.find(l => l.Scope === EntityScopeEnum.Global) ?? layouts[0];
        }
      }
      if (!layout) {
        this.currentLayoutStateSubject.next({state, loadOptions: LoadLayoutFaultsOptions.None});
      }
      if (layout?.Id && (!prevState || prevState?.project?.MyDocmaUid !== state.project.MyDocmaUid || prevState?.layout?.Id !== layout.Id)) {
        return await this.setLayoutInternal(state, layout, true, true);
      }
    } else {
      this.currentLayoutStateSubject.next({state, loadOptions: LoadLayoutFaultsOptions.None});
    }
  }

  private async setLayoutInternal(model: LayoutStateModel, layout: Layout, notifyState: boolean = true, isFirstProjectLayout = false): Promise<LayoutStateModel> {

    try {
      if (layout) {
        this.prevSelectedLayout = layout;
      }

      await this.getLayoutStateModel(model, layout);

      if (isFirstProjectLayout) {
        this.defaultLayoutHasNoFields = model && model?.layout?.Fields?.length === 0;
        if (this.defaultLayoutHasNoFields && !this.deviceService.isMobil() && this.router.url.includes('/faults')) {
          const layoutLeftPane = buttonsMap[MmSideNavBarButtonsEnum.FilterLayout].button as ContentSideNavButton;
          this.appContainerService.openContentSideNav(layoutLeftPane.id, layoutLeftPane.content);
        }
      }

      // console.debug('LayoutState changed, after setting');
      // console.debug(model);

      if (notifyState) {
        if (model) {
          const currentProject = await firstValueFrom(this.mmProjectService.currentProject);
          const filterModel = await this.personalizationService.getProjectValue(currentProject.MyDocmaUid, `${PersonalizationKeysEnum.FaultsGrid}.FilterModel_${model.layout.Id}`);
          if (filterModel) {
            if (this.gridApi) {
              this.gridApi.setFilterModel(filterModel);
              const filters = this.filterService.convertToLayoutFilter(filterModel, model);
              model = await this.addFilter(filters, model);
            } else {
              console.warn('GridApi not yet initialized when trying to set filter model in layout-state.service setLayoutInternal');
            }
          }
        }

        this.currentLayoutStateSubject.next({state: model, loadOptions: LoadLayoutFaultsOptions.None});
      }
      return model;
    } catch (ex) {
      throw ex;
    }
  }

  public async getLayoutStateModel(model: LayoutStateModel, layout: Layout, forceRefresh = false, isCurrentLayout = true) {
    const downloaded = await this.mmDownloadService.downloadLayout(model.project, layout.Id, forceRefresh).toPromise();

    model.layout = downloaded;
    model.filters = [];
    // model.groups = downloaded.Fields?.filter(value => value.Grouping)?.map(value => ({field: value.Field.DefaultName, groupSort: (value.IsDescending ? GroupSortType.desc : GroupSortType.asc)}) );
    model.sortItems = [];
    model.userState = await this.personalizationService.getValue(`${downloaded.SUid}.${PersonalizationKeysEnum.LayoutState}`);
    model.userColumnOrder = await this.personalizationService.getValue(`${downloaded.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`);
    if (!model.userState) {
      model.userState = {};
    }

    if (isCurrentLayout) {
      this.prevSelectedLayout = layout;
      await this.personalizationService.setProjectValue(model.project.MyDocmaUid,
        PersonalizationKeysEnum.LastLayoutId, {Id: downloaded.Id, Name: downloaded.Name});
    }
  }
  async updateLayout(layout: Layout): Promise<LayoutStateModel> {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    if (model.project  && layout &&  isIdsEquals(model.layout, layout)) {
      return await this.setLayoutInternal(model, layout, false);
    }
    return model;
  }
  async setLayout(layout: Layout, forceSet: boolean = false): Promise<LayoutStateModel> {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    if ((model.project  && layout &&  !isIdsEquals(model.layout, layout)) || forceSet) {
      this.defaultLayoutHasNoFields = false;
      return await this.setLayoutInternal(model, layout);
    }
    return model;
  }

  async addFilter(filters: FilterCriteria[], layoutState?: LayoutStateModel) {
    const model = new LayoutStateModel((layoutState != null ? layoutState : this.currentLayoutStateSubject.value.state));
    if (model.project  && model.layout ) {
      if (( !model.filters  || model.filters.length === 0) &&
        ( !filters || filters.length === 0) ) {
        return model;
      }
      model.filters = filters;
    }
    return model;
  }

  async setFilter(filters: FilterCriteria[], layoutState?: LayoutStateModel, isTileView: boolean = false) {
    const model = await this.addFilter(filters, layoutState);
    if (isTileView) {
      const filterModel = this.filterService.convertToGridFilter(model.filters);
      const currentProject = await firstValueFrom(this.mmProjectService.currentProject);
      this.personalizationService.setProjectValue(currentProject.MyDocmaUid, `${PersonalizationKeysEnum.FaultsGrid}.FilterModel_${model.layout.Id}`, filterModel);
    }
    this.currentLayoutStateSubject.next({state: model, loadOptions: LoadLayoutFaultsOptions.None});

    return model;
  }

  async setSearchFilter(filters: FilterCriteria[]) {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    if (model.project  && model.layout ) {
      if (( !model.searchFilters  || model.searchFilters.length === 0) &&
        ( !filters || filters.length === 0) ) {
        return model;
      }
      model.searchFilters = filters;
      console.debug('LayoutState changed, after searchIds-filter');
      console.debug(model);

      this.currentLayoutStateSubject.next({state: model, loadOptions: LoadLayoutFaultsOptions.None});
    }
    return model;
  }


  async setLayoutFilterState(filterDisabled: boolean) {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    if (model.project  && model.layout ) {
      model.layout.FilterDisabled = filterDisabled;
      this.currentLayoutStateSubject.next({state: model, loadOptions: LoadLayoutFaultsOptions.None});
    }
    return model;
  }

  async setSorting(sortItems: LayoutSortItem[]) {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    if (model.project  && model.layout ) {
      if (( !model.sortItems  || model.sortItems.length === 0) && (!sortItems || sortItems.length === 0) ) {
        return model;
      }
      model.sortItems = sortItems;
      // console.debug('LayoutState changed, after sorting');
      // console.debug(model);

      this.currentLayoutStateSubject.next({state: model, loadOptions: LoadLayoutFaultsOptions.EnsureVisibleAfterLoad});
    }
    return model;
  }

  async getLayoutStateOfLayout(layout: Layout): Promise<Layout> {
    return await this.personalizationService.getValue(`${layout.SUid}.${PersonalizationKeysEnum.LayoutState}`);
  }

  async getLayoutColumnOrderOfLayout(layout: Layout): Promise<FieldOrder[]> {
    return await this.personalizationService.getValue(`${layout.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`);
  }

  async deleteLayoutStateOfLayout(layout: Layout) {
    await this.personalizationService.deleteValue(`${layout.SUid}.${PersonalizationKeysEnum.LayoutState}`);
    await this.personalizationService.deleteValue(`${layout.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`);
    this.userStateChangedSubject.next(null);
  }


  async setLayoutState(state: Layout) {
    if (!this.currentLayoutStateSubject?.value?.state) { return; }

    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);

    if (!model?.layout?.SUid) { return; }     // BUGFIX: local mode layout is selected

    if (state) {
      model.userState = state;
      await this.personalizationService.setValue(`${model.layout.SUid}.${PersonalizationKeysEnum.LayoutState}`, state);
    }
    else {
      await this.personalizationService.deleteValue(`${model.layout.SUid}.${PersonalizationKeysEnum.LayoutState}`);
    }

    this.userStateChangedSubject.next(state);
  }

  async setLayoutColumnOrder(state: any) {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    model.userColumnOrder = state;
    await this.personalizationService.setValue(`${model.layout.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`, state);

    this.columnOrderChangedSubject.next(state);
  }

  async getLayoutColumnOrder() {
    const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
    return await this.personalizationService.getValue(`${model.layout.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`);
  }

  deleteColumnOrder() {
    if (this.currentLayoutStateSubject?.value) {
      const model = new LayoutStateModel(this.currentLayoutStateSubject.value.state);
      model.userColumnOrder = null;
      if (!!model?.layout?.SUid) {
        this.personalizationService.deleteValue(`${model.layout.SUid}.${PersonalizationKeysEnum.LayoutColumnOrder}`);
        this.columnOrderChangedSubject.next(model.layout);
      }
    }
  }

  public getGroups(layoutState: LayoutStateModel): GroupByCol[] {
    const fieldsDict: {[id: number]: string} = {};
    (layoutState?.layout?.Fields ?? []).map(f => fieldsDict[f.Field.Id] = f.Field.DefaultName);

    const grouping: {field: string, groupSort: GroupSortType, grouping: number}[] = [];
    const layoutGroupingFields = (layoutState?.layout?.Fields?.filter(x => x.Grouping !== 0) ?? []);

    for (const group of layoutGroupingFields) {
      const f = layoutState.userState?.Fields?.find(x => fieldsDict[x.Field.Id] === group.Field.DefaultName);
      if (f) {
        if ((f.Grouping ?? 0) !== 0) {
          grouping.push({field: fieldsDict[f.Field.Id], groupSort: (f.IsDescending ? GroupSortType.desc : GroupSortType.asc), grouping: f.Grouping});
        }
      }
      else {
        const field = (layoutState?.layout?.Fields ?? []).find(x => x.Field.DefaultName === group.Field.DefaultName);
        grouping.push({field: fieldsDict[field.Field.Id], groupSort: (field.IsDescending ? GroupSortType.desc : GroupSortType.asc), grouping: field.Grouping});
      }
    }

    for (const field of (layoutState?.userState?.Fields ?? [])) {
      const g = layoutGroupingFields.find(x => fieldsDict[field.Field.Id] === x.Field.DefaultName);
      if (!g && (field.Grouping ?? 0) !== 0) {
        grouping.push({field: fieldsDict[field.Field.Id], groupSort: (field.IsDescending ? GroupSortType.desc : GroupSortType.asc), grouping: field.Grouping});
      }
    }

    return grouping.sort((a, b) => a.grouping - b.grouping).map(m => ({colId: m.field, groupSort: m.groupSort}));
  }

  ngOnDestroy(): void {
    this.pendingService.removePendingState(this.loadingLayoutDef);
  }

}
